/**
 * Competency and training, as the client sees it.
 *
 * A competency is something staff must be able to do — hand hygiene
 * technique, restraint application, the new infusion pump — and prove they
 * can. Publishing one assigns it to every member it applies to, with a
 * due date; each person then submits evidence (an attestation, a file, or
 * both), which a manager may have to approve. Surveyors ask for exactly
 * this: who was required to, who did, and what the proof was.
 *
 * Shapes mirror functions/src/competencyEvidence.
 */

import { hasAtLeast, type Role } from '../../types/roles';

export const COMPETENCY_STATUSES = ['draft', 'published', 'retired'] as const;
export type CompetencyStatus = (typeof COMPETENCY_STATUSES)[number];
export const STATUS_LABEL: Record<CompetencyStatus, string> = { draft: 'Draft', published: 'Published', retired: 'Retired' };
export const STATUS_TONE: Record<CompetencyStatus, 'neutral' | 'ok'> = { draft: 'neutral', published: 'ok', retired: 'neutral' };

export const ASSIGNMENT_STATUSES = ['pending', 'completed', 'overdue', 'waived'] as const;
export type AssignmentStatus = (typeof ASSIGNMENT_STATUSES)[number];
export const ASSIGNMENT_LABEL: Record<AssignmentStatus, string> = {
  pending: 'To do', completed: 'Completed', overdue: 'Overdue', waived: 'Waived',
};
export const ASSIGNMENT_TONE: Record<AssignmentStatus, 'neutral' | 'ok' | 'warn' | 'danger'> = {
  pending: 'warn', completed: 'ok', overdue: 'danger', waived: 'neutral',
};

function member<T extends readonly string[]>(list: T, v: unknown, fallback: T[number]): T[number] {
  return (list as readonly unknown[]).includes(v) ? (v as T[number]) : fallback;
}
export const toStatus = (v: unknown) => member(COMPETENCY_STATUSES, v, 'draft');
export const toAssignmentStatus = (v: unknown) => member(ASSIGNMENT_STATUSES, v, 'pending');

/** When in a person's employment the competency is owed. */
export const COMPETENCY_TYPES = ['initial', 'annual', 'ongoing', 'unit_specific', 'equipment'] as const;
export type CompetencyType = (typeof COMPETENCY_TYPES)[number];
export const COMPETENCY_TYPE_LABEL: Record<CompetencyType, string> = {
  initial: 'Initial (orientation)', annual: 'Annual', ongoing: 'Ongoing', unit_specific: 'Unit-specific', equipment: 'Equipment',
};
export const toCompetencyType = (v: unknown) => member(COMPETENCY_TYPES, v, 'ongoing');

/** How competence is validated; the method a surveyor asks to see named. */
export const VALIDATION_METHODS = [
  'direct_observation', 'return_demonstration', 'simulation', 'written_test', 'case_review', 'peer_review', 'certification',
] as const;
export type ValidationMethod = (typeof VALIDATION_METHODS)[number];
export const VALIDATION_METHOD_LABEL: Record<ValidationMethod, string> = {
  direct_observation: 'Direct observation',
  return_demonstration: 'Return demonstration',
  simulation: 'Simulation',
  written_test: 'Written test',
  case_review: 'Case review',
  peer_review: 'Peer review',
  certification: 'External certification',
};
export const toValidationMethods = (v: unknown): ValidationMethod[] =>
  Array.isArray(v) ? v.filter((x): x is ValidationMethod => (VALIDATION_METHODS as readonly unknown[]).includes(x)) : [];

export interface EvidenceRequirements {
  attestation: boolean;
  fileUpload: boolean;
  managerApproval: boolean;
  attestationText: string | null;
}

export const NO_REQUIREMENTS: EvidenceRequirements = {
  attestation: true, fileUpload: false, managerApproval: false, attestationText: null,
};

/** The requirements in words, for a list cell. */
export function requirementsText(r: EvidenceRequirements): string {
  const parts: string[] = [];
  if (r.attestation) parts.push('attestation');
  if (r.fileUpload) parts.push('file');
  if (r.managerApproval) parts.push('manager approval');
  return parts.length ? parts.join(' + ') : 'none';
}

export interface CompetencySummary {
  id: string;
  title: string;
  description: string | null;
  status: CompetencyStatus;
  appliesToRoles: string[];
  appliesToDepartments: string[];
  renewalIntervalDays: number | null;
  competencyType: CompetencyType;
  validationMethods: ValidationMethod[];
  currentVersionNumber: number | null;
  updatedAt: string | null;
}

export interface CompetencyDetail extends CompetencySummary {
  evidenceRequirements: EvidenceRequirements;
  currentVersionId: string | null;
  createdAt: string | null;
  publishedAt: string | null;
  retiredAt: string | null;
}

export interface CompetencyVersion {
  id: string;
  versionNumber: number;
  publishedAt: string | null;
}

export interface CompetencyEvent {
  id: string;
  action: string;
  actorEmail: string | null;
  details: Record<string, unknown> | null;
  createdAt: string | null;
}

export interface Assignment {
  id: string;
  competencyId: string;
  competencyVersionNumber: number;
  competencyTitle: string;
  userId: string;
  userEmail: string | null;
  userDisplayName: string | null;
  status: AssignmentStatus;
  dueDate: string | null;
  assignedAt: string | null;
  completedAt: string | null;
  waivedAt: string | null;
  waivedReason: string | null;
  evidenceId: string | null;
}

export interface Evidence {
  id: string;
  assignmentId: string;
  competencyId: string;
  userId: string;
  userEmail: string | null;
  userDisplayName: string | null;
  submittedAt: string | null;
  attestationText: string | null;
  fileUrls: string[];
  notes: string | null;
  /** Who validated it, when ("YYYY-MM-DD"), and the score if there was one. */
  assessorName: string | null;
  assessmentDate: string | null;
  score: number | null;
  managerApproval: { approved: boolean; approvedByEmail: string | null; approvedAt: string | null; comments: string | null } | null;
}

export const EVENT_LABEL: Record<string, string> = {
  created: 'Created',
  updated: 'Edited',
  published: 'Published',
  republished: 'New version published',
  retired: 'Retired',
  assignments_created: 'Assigned to staff',
  evidence_submitted: 'Evidence submitted',
  evidence_approved: 'Evidence approved',
  evidence_rejected: 'Evidence rejected',
  assignment_waived: 'Assignment waived',
};
export const eventLabel = (a: string) => EVENT_LABEL[a] ?? a.replace(/_/g, ' ');

export interface CompetencyFields {
  title: string;
  description: string;
  appliesToRoles: string[];
  appliesToDepartments: string[];
  attestation: boolean;
  attestationText: string;
  fileUpload: boolean;
  managerApproval: boolean;
  /** Empty means none. */
  renewalIntervalDays: string;
  competencyType: CompetencyType;
  validationMethods: ValidationMethod[];
}

export const EMPTY_FIELDS: CompetencyFields = {
  title: '', description: '', appliesToRoles: [], appliesToDepartments: [],
  attestation: true, attestationText: '', fileUpload: false, managerApproval: false,
  renewalIntervalDays: '', competencyType: 'ongoing', validationMethods: [],
};

/** The attestation wording when the competency does not supply its own. */
export const DEFAULT_ATTESTATION =
  'I attest that I have completed this competency and can perform it as required.';

/** Mirrors the callables' status and role checks. */
export function competencyActions(status: CompetencyStatus, roles: readonly Role[]) {
  const author = hasAtLeast(roles, 'author');
  const admin = hasAtLeast(roles, 'admin');
  return {
    edit: author && status === 'draft',
    publish: admin && (status === 'draft' || status === 'published'),
    retire: admin && status !== 'retired',
  };
}

/** What may be done to one assignment: submit (its owner), waive (an admin). */
export function assignmentActions(a: Assignment, roles: readonly Role[], uid: string | null) {
  const open = a.status === 'pending' || a.status === 'overdue';
  return {
    submit: open && !!uid && a.userId === uid && !a.evidenceId,
    awaitingApproval: open && !!a.evidenceId,
    waive: open && hasAtLeast(roles, 'admin'),
  };
}
